import React, { Fragment } from 'react';
import { useParams } from 'react-router-dom';
import * as storeExt from '../store';
import * as utils from '../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Tooltip } from 'react-tooltip';

const { useGetConfigQuery, useGetProjectQuery } = storeExt;

const DisplayProjectSiteInfo = (props) => {
    const { project } = props;
    const hasSiteInfo = !(
        project?.siteInfo === null || project?.siteInfo === undefined
    );
    const hasSiteInfoCity =
        hasSiteInfo &&
        !(
            project.siteInfo.city === null ||
            project.siteInfo.city === undefined
        );
    const hasSiteInfoJson = project?.siteInfoConfiguratorJson;
    const hasOldSiteInfo = hasSiteInfo && !hasSiteInfoCity && hasSiteInfoJson;

    const oldSiteInfo = React.useMemo(() => {
        if (hasOldSiteInfo) {
            const jsonObj = JSON.parse(project.siteInfoConfiguratorJson);
            const siteInfoStr = jsonObj.fields?.find(
                (x) => x.name === 'fVM',
            )?.stringValue;
            const siteInfo = siteInfoStr ? JSON.parse(siteInfoStr) : undefined;
            return siteInfo;
        }
        return null;
    }, [project, hasOldSiteInfo]);

    const renderOldSiteInfo = (oldSiteInfo) => {
        return (
            <div className="text-sm">
                <div>
                    {`${oldSiteInfo.location.city}, ${
                        oldSiteInfo.location.state
                    } ${oldSiteInfo.location.zipCode ?? ''}`}
                </div>
                <div>
                    {oldSiteInfo.seismicLoadCalculationParameters
                        .siteClassificationLetter
                        ? `Class ${oldSiteInfo.seismicLoadCalculationParameters.siteClassificationLetter}`
                        : ''}
                </div>
            </div>
        );
    };

    const renderNewSiteInfo = (siteInfo) => {
        if (siteInfo) {
            return (
                <div className="text-sm">
                    <div>
                        {`${project.siteInfo.city} ${project.siteInfo.state} `}
                    </div>
                    <div>
                        {project.siteInfo.siteClass
                            ? `Class ${project.siteInfo.siteClass}`
                            : ''}
                    </div>
                </div>
            );
        } else {
            return <div>Add Site Conditions</div>;
        }
    };

    return (
        <React.Fragment>
            {hasOldSiteInfo && renderOldSiteInfo(oldSiteInfo)}
            {!hasOldSiteInfo && renderNewSiteInfo(project?.siteInfo)}
        </React.Fragment>
    );
};

export const ProjectHeader = (props) => {
    const {
        parentPath,
        isUpdatingProjectName,
        updatedProjectName,
        setIsUpdatingProjectName,
        setUpdatedProjectName,
        handleNameUpdate,
        onSiteInfoConfiguratorLoaded,
        handleSiteInfoUpdate,
        setExportState,
        headerOpsDisable,
        title,
    } = props;
    const [, setCpqData] = utils.cpqDataState.useStateContext();
    const { projectId } = useParams();
    const { data: config } = useGetConfigQuery();
    const { data: project } = useGetProjectQuery(projectId);

    const renderOpToolTip = (placeHolderId, opPhase) => {
        return (
            <Tooltip
                anchorSelect={`#${placeHolderId}`}
                place="top"
            >
                {'Unable to ' + opPhase + ' while document build in progress'}
            </Tooltip>
        );
    };

    return (
        <div
            className="flex py-4 text-lg items-center"
            title={title}
        >
            <div className="flex items-center border-r border-green-dark pr-4 mr-4">
                <div className="mr-2">
                    <span className="fa-layers fa-fw">
                        <FontAwesomeIcon
                            icon="fa-solid fa-star"
                            size="2xs"
                        />
                        <FontAwesomeIcon
                            icon="fa-regular fa-folder"
                            size="xl"
                        />
                    </span>
                </div>
                {parentPath === 'projects' ? (
                    isUpdatingProjectName ? (
                        <input
                            type="text"
                            onBlur={handleNameUpdate}
                            onChange={(e) =>
                                setUpdatedProjectName(e.target.value)
                            }
                            value={updatedProjectName}
                        />
                    ) : (
                        <Fragment>
                            <div
                                className={classNames(
                                    'text-green-dark',
                                    headerOpsDisable
                                        ? 'cursor-not-allowed'
                                        : 'cursor-pointer',
                                )}
                                onClick={() =>
                                    headerOpsDisable
                                        ? null
                                        : setIsUpdatingProjectName(true)
                                }
                                id="projectName-id"
                            >
                                {project?.name ?? 'Project'}
                            </div>
                            {headerOpsDisable &&
                                renderOpToolTip(
                                    'projectName-id',
                                    'change project name',
                                )}
                        </Fragment>
                    )
                ) : (
                    <div className="text-green-dark">
                        {project?.name ?? 'Project'}
                    </div>
                )}
            </div>
            <div className="flex items-center">
                <div className="mr-2">
                    <FontAwesomeIcon
                        icon="fa-solid fa-map-location-dot"
                        size="lg"
                    />
                </div>
                {parentPath === 'projects' ? (
                    <Fragment>
                        <div
                            className={classNames(
                                'underline text-green-dark',
                                headerOpsDisable
                                    ? 'cursor-not-allowed'
                                    : 'cursor-pointer',
                            )}
                            onClick={() =>
                                headerOpsDisable
                                    ? null
                                    : setCpqData({
                                          showModal: true,
                                          configuratorId:
                                              config?.configurators
                                                  ?.SeismicAndWind,
                                          onConfiguratorLoaded:
                                              onSiteInfoConfiguratorLoaded,
                                          onSubmit: handleSiteInfoUpdate,
                                      })
                            }
                            id="projectSiteInfo-id"
                        >
                            <DisplayProjectSiteInfo project={project} />
                        </div>
                        {headerOpsDisable &&
                            renderOpToolTip(
                                'projectSiteInfo-id',
                                "change project's info",
                            )}
                    </Fragment>
                ) : (
                    <div className="underline text-green-dark">
                        <DisplayProjectSiteInfo project={project} />
                    </div>
                )}
            </div>
            {parentPath === 'documents' ? (
                <React.Fragment>
                    <div className="flex-grow ml-4">&nbsp;</div>
                    <div
                        className={classNames(
                            'flex ml-4 mr-4 w-28 h-10 border-2 text-base font-bold justify-center items-center',
                            headerOpsDisable
                                ? 'border-gray-30 text-gray-30 hover:cursor-not-allowed'
                                : 'border-green-primary text-green-primary hover:cursor-pointer',
                        )}
                        onClick={() =>
                            headerOpsDisable ? null : setExportState(true)
                        }
                        id="export-id"
                    >
                        EXPORT
                    </div>
                    {headerOpsDisable &&
                        renderOpToolTip(
                            'export-id',
                            'generate project document',
                        )}
                </React.Fragment>
            ) : null}
        </div>
    );
};
